import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, ChevronLeft, CheckCircle, Camera, Video, X, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const MobileIQC = ({ user, qcType = 'IQC' }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('scan');
  const [device, setDevice] = useState(null);
  const [template, setTemplate] = useState(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [calculatedGrade, setCalculatedGrade] = useState(null);
  const [images, setImages] = useState([]);
  const [videoBlob, setVideoBlob] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [partsQuantities, setPartsQuantities] = useState({});
  const [isDeadPhone, setIsDeadPhone] = useState(false);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  const DeviceScan = () => {
    const [scanInput, setScanInput] = useState('');
    const [assignedDevices] = useState([
      { did: 'DID001', sku: 'SAMSUNG-GALAXYS21-128GB', brand: 'Samsung', model: 'Galaxy S21', assigned_at: '2 hours ago' },
      { did: 'DID002', sku: 'APPLE-IPHONE13-128GB', brand: 'Apple', model: 'iPhone 13', assigned_at: '1 hour ago' },
    ]);

    const handleDeviceSelect = async (selectedDevice) => {
      setDevice(selectedDevice);
      await loadTemplate(selectedDevice.brand.toLowerCase().includes('apple') ? 'iOS' : 'Android');
    };

    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-6 mb-4 border-t-4" style={{ borderTopColor: ZOBOX_BLUE }}>
            <h2 className="text-xl font-bold mb-2" style={{ color: ZOBOX_BLUE }}>Quality Check</h2>
            <p className="text-sm text-gray-600 mb-4">Scan device or select from list</p>
            <input
              type="text"
              placeholder="Scan DID..."
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  const found = assignedDevices.find(d => d.did === scanInput);
                  if (found) handleDeviceSelect(found);
                }
              }}
              className="w-full px-4 py-3 border-2 rounded-lg text-base focus:outline-none focus:ring-2 focus:border-blue-500"
              autoFocus
            />
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3 text-base">Assigned Devices</h3>
            {assignedDevices.map((dev) => (
              <button
                key={dev.did}
                onClick={() => handleDeviceSelect(dev)}
                className="w-full text-left p-4 mb-2 border-2 border-gray-200 rounded-lg hover:border-blue-600 hover:shadow-md transition-all"
              >
                <div className="font-bold text-base" style={{ color: ZOBOX_BLUE }}>{dev.did}</div>
                <div className="text-sm text-gray-700 mt-1">{dev.brand} {dev.model}</div>
                <div className="text-xs text-gray-500 mt-1">{dev.sku}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const loadTemplate = async (os) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/iqc/templates/${os}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(response.data);
      setStep('iqc');
    } catch (error) {
      console.error('Error loading template:', error);
      alert('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const IQCForm = () => {
    if (!template || !template.categories) return null;
    
    const currentCategory = template.categories[currentCategoryIndex];
    const progress = ((currentCategoryIndex + 1) / template.categories.length) * 100;
    const answeredInCategory = currentCategory.parameters.filter(
      p => responses[p.parameter_id]
    ).length;

    const handleOptionSelect = (parameterId, option, parameter) => {
      // Check for Dead Phone logic
      if (parameterId === '1A' && option.value === 'Dead Phone (DOA)') {
        setIsDeadPhone(true);
      } else if (parameterId === '1A' && option.value !== 'Dead Phone (DOA)') {
        setIsDeadPhone(false);
      }

      setResponses(prev => ({
        ...prev,
        [parameterId]: {
          parameter_id: parameterId,
          parameter_name: parameter.name,
          parameter_number: parameter.parameter_number,
          selected_value: option.value,
          triggered_parts: option.triggers_parts || [],
          triggers_engineer: option.triggers_engineer || null
        }
      }));

      // Initialize parts quantities
      if (option.triggers_parts && option.triggers_parts.length > 0) {
        const newQuantities = {};
        option.triggers_parts.forEach(part => {
          if (!partsQuantities[part]) {
            newQuantities[part] = 1;
          }
        });
        setPartsQuantities(prev => ({ ...prev, ...newQuantities }));
      }
    };

    const canProceed = currentCategory.parameters.every(
      p => !p.is_required || responses[p.parameter_id]
    );

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="shadow-lg sticky top-0 z-10" style={{ backgroundColor: ZOBOX_BLUE }}>
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-white">
                <div className="text-sm font-bold">{device.did}</div>
                <div className="text-xs opacity-90">{device.brand} {device.model}</div>
              </div>
              <button onClick={() => setStep('scan')} className="text-xs text-white bg-red-500 px-3 py-1 rounded">
                Cancel
              </button>
            </div>
            
            <div className="flex items-center justify-between text-xs text-white mb-2">
              <span className="font-medium">{currentCategory.name}</span>
              <span className="bg-white text-blue-900 px-2 py-0.5 rounded font-bold">
                {currentCategoryIndex + 1}/{template.categories.length}
              </span>
            </div>
            <div className="w-full bg-blue-300 rounded-full h-2">
              <div className="bg-white h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="text-xs text-white mt-1">
              {answeredInCategory}/{currentCategory.parameters.length} completed
            </div>
          </div>
        </div>

        <div className="p-4 pb-24">
          {isDeadPhone && currentCategoryIndex > 0 && (
            <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 mb-4 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-sm font-bold text-red-900 mb-1">Dead Phone Detected</h3>
                <p className="text-xs text-red-700">This device is marked as Dead Phone (DOA). All subsequent tests are being skipped. Device will be recommended to L4 Profile Engineer for diagnosis.</p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-lg p-5 mb-4 border-l-4" style={{ borderLeftColor: ZOBOX_BLUE }}>
            <div className="flex items-center gap-3">
              <span className="text-3xl">{currentCategory.icon}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-black px-3 py-1 rounded text-white" style={{ backgroundColor: ZOBOX_BLUE }}>
                    {currentCategory.category_number}
                  </span>
                  <h2 className="text-lg font-bold text-gray-800">{currentCategory.name}</h2>
                </div>
              </div>
            </div>
          </div>

          {currentCategory.parameters.map((parameter) => {
            const response = responses[parameter.parameter_id];
            const isAnswered = !!response;
            const isSkipped = isDeadPhone && currentCategoryIndex > 0;

            return (
              <div key={parameter.parameter_id} className={`bg-white rounded-xl shadow-md p-4 mb-4 border-2 ${isSkipped ? 'opacity-50' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between mb-3 pb-3 border-b-2 border-gray-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg font-black px-3 py-1 rounded-lg text-white" style={{ backgroundColor: ZOBOX_BLUE }}>
                        {parameter.parameter_number}
                      </span>
                      <h3 className="text-base font-semibold text-gray-900">
                        {parameter.name}
                        {parameter.is_required && <span className="text-red-500 ml-1">*</span>}
                      </h3>
                    </div>
                  </div>
                  {isAnswered && (
                    <CheckCircle className="w-6 h-6 flex-shrink-0" style={{ color: ZOBOX_BLUE }} />
                  )}
                </div>

                <div className="space-y-2">
                  {parameter.options.map((option, idx) => {
                    const isSelected = response?.selected_value === option.value;
                    const hasEngineer = option.triggers_engineer;

                    return (
                      <button
                        key={idx}
                        onClick={() => !isSkipped && handleOptionSelect(parameter.parameter_id, option, parameter)}
                        disabled={isSkipped}
                        className="w-full text-left p-3 rounded-lg border-2 transition-all text-sm font-medium relative"
                        style={{
                          borderColor: isSelected ? ZOBOX_BLUE : '#e5e7eb',
                          backgroundColor: isSelected ? '#eff6ff' : 'white',
                          color: isSelected ? ZOBOX_BLUE : '#374151'
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-xs font-bold text-gray-500 mt-0.5">{option.option_number ||`${parameter.parameter_number}.${idx + 1}`}</span>
                          <span className="flex-1">{option.value}</span>
                        </div>
                        {hasEngineer && (
                          <div className="text-xs mt-2 px-2 py-1 bg-yellow-100 text-yellow-800 rounded inline-block">
                            → Assign to {hasEngineer} Engineer
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 shadow-lg p-4" style={{ borderTopColor: ZOBOX_BLUE }}>
          <div className="max-w-md mx-auto flex gap-3">
            {currentCategoryIndex > 0 && (
              <button
                onClick={() => setCurrentCategoryIndex(prev => prev - 1)}
                className="flex-1 px-4 py-3 text-white rounded-lg flex items-center justify-center gap-2 text-sm font-bold"
                style={{ backgroundColor: '#6b7280' }}
              >
                <ChevronLeft className="w-5 h-5" /> Previous
              </button>
            )}
            <button
              onClick={() => {
                if (isDeadPhone) {
                  // Dead Phone: Skip directly to media capture
                  setStep('media');
                } else if (currentCategoryIndex < template.categories.length - 1) {
                  setCurrentCategoryIndex(prev => prev + 1);
                } else {
                  calculateGrade();
                  setStep('review');
                }
              }}
              disabled={!canProceed}
              className="flex-1 px-4 py-3 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 text-sm font-bold"
              style={{ backgroundColor: ZOBOX_BLUE }}
            >
              {isDeadPhone ? 'Capture Media' : (currentCategoryIndex === template.categories.length - 1 ? 'Review Summary' : 'Next')}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const calculateGrade = () => {
    const gradeScores = { excellent: 0, superb: 0, good: 0, fair: 0, okay: 0 };

    Object.values(responses).forEach(response => {
      if (response.grades) {
        Object.keys(response.grades).forEach(grade => {
          gradeScores[grade] += response.grades[grade];
        });
      }
    });

    let finalGrade = 'Okay';
    if (gradeScores.excellent === 0) finalGrade = 'Excellent';
    else if (gradeScores.superb === 0) finalGrade = 'Superb';
    else if (gradeScores.good === 0) finalGrade = 'Good';
    else if (gradeScores.fair === 0) finalGrade = 'Fair';

    setCalculatedGrade({ grade: finalGrade, scores: gradeScores });
  };

  const PartsReview = () => {
    // Aggregate parts from all responses (part-centric view)
    const partsMap = {};
    Object.values(responses).forEach(resp => {
      if (resp.triggered_parts && resp.triggered_parts.length > 0) {
        resp.triggered_parts.forEach(part => {
          if (!partsMap[part]) {
            partsMap[part] = {
              name: part,
              parameters: [],
              selected: true // Default to selected
            };
          }
          partsMap[part].parameters.push({
            number: resp.parameter_number,
            name: resp.parameter_name,
            value: resp.selected_value
          });
        });
      }
    });

    // Complete Housing includes: Back Panel, Chrome, Middle Ring, Buttons, Camera Lens
    const housingParts = ['Back Panel', 'Chrome', 'Middle Ring', 'Buttons', 'Camera Lens'];
    const hasHousingParts = housingParts.filter(p => partsMap[p]);
    const housingParams = [];
    
    let shouldUseHousing = false;
    if (hasHousingParts.length >= 1) {
      // If ANY housing part needed, use complete housing instead
      shouldUseHousing = true;
      housingParts.forEach(p => {
        if (partsMap[p]) {
          housingParams.push(...partsMap[p].parameters);
          delete partsMap[p];
        }
      });
      partsMap['Complete Housing'] = {
        name: 'Complete Housing',
        parameters: housingParams,
        selected: true,
        isBundle: true,
        bundleIncludes: 'Back Panel + Chrome + Middle Ring + Buttons + Camera Lens'
      };
    }

    // Initialize quantities for all parts if not set
    React.useEffect(() => {
      const newQuantities = {};
      Object.keys(partsMap).forEach(partName => {
        if (!partsQuantities[partName]) {
          newQuantities[partName] = 1;
        }
      });
      if (Object.keys(newQuantities).length > 0) {
        setPartsQuantities(prev => ({ ...prev, ...newQuantities }));
      }
    }, []);

    const togglePart = (partName) => {
      setPartsQuantities(prev => {
        const current = prev[partName] || 1;
        return {
          ...prev,
          [partName]: current > 0 ? 0 : 1
        };
      });
    };

    const updatePartQuantity = (partName, delta) => {
      setPartsQuantities(prev => {
        const current = prev[partName] || 1;
        const newQty = Math.max(1, current + delta);
        console.log(`Updating ${partName}: ${current} -> ${newQty}`);
        return {
          ...prev,
          [partName]: newQty
        };
      });
    };

    return (
      <div className="min-h-screen bg-gray-50 p-4 pb-24">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-5 mb-4 border-t-4" style={{ borderTopColor: ZOBOX_BLUE }}>
            <h2 className="text-xl font-bold mb-2" style={{ color: ZOBOX_BLUE }}>Part Request</h2>
            <p className="text-sm text-gray-600">Select parts to be requested</p>
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-800">
                ℹ️ Final grade and cost analysis will be determined by the approver.
              </p>
            </div>
          </div>

          {Object.keys(partsMap).length === 0 ? (
            <div className="bg-green-50 border-2 border-green-500 rounded-xl p-6 text-center">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-600" />
              <h3 className="text-base font-bold text-green-900 mb-1">No Parts Required</h3>
              <p className="text-sm text-green-700">Device passed all quality checks</p>
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(partsMap).map(([partName, partData]) => {
                const isSelected = partsQuantities[partName] !== 0;
                const quantity = partsQuantities[partName] || 1;
                
                return (
                  <div 
                    key={partName} 
                    className={`bg-white rounded-xl shadow-md p-4 border-2 transition-all ${isSelected ? 'border-blue-500' : 'border-gray-200'}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => togglePart(partName)}
                        className="mt-1 w-5 h-5"
                        style={{ accentColor: ZOBOX_BLUE }}
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-sm font-bold text-gray-900">
                            {partName}
                            {partData.isBundle && (
                              <span className="ml-2 text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded">Bundle</span>
                            )}
                          </h3>
                        </div>
                        
                        {partData.bundleIncludes && (
                          <div className="text-xs text-gray-500 mb-2 bg-orange-50 p-2 rounded">
                            Includes: {partData.bundleIncludes}
                          </div>
                        )}
                        
                        <div className="mt-2 space-y-1 mb-3">
                          {partData.parameters.map((param, idx) => (
                            <div key={idx} className="text-xs text-gray-600">
                              <span className="font-semibold">{param.number}</span>: {param.value}
                            </div>
                          ))}
                        </div>

                        <div className={`flex items-center gap-3 pt-2 border-t ${!isSelected ? 'opacity-50' : ''}`}>
                          <span className="text-xs text-gray-600 font-medium">Quantity:</span>
                          <button
                            onClick={() => updatePartQuantity(partName, -1)}
                            disabled={quantity <= 1 || !isSelected}
                            className="w-7 h-7 rounded-lg bg-gray-200 flex items-center justify-center text-sm font-bold disabled:opacity-50"
                          >
                            -
                          </button>
                          <span className="text-base font-bold w-8 text-center" style={{ color: ZOBOX_BLUE }}>
                            {quantity}
                          </span>
                          <button
                            onClick={() => updatePartQuantity(partName, 1)}
                            disabled={!isSelected}
                            className="w-7 h-7 rounded-lg text-white flex items-center justify-center text-sm font-bold disabled:opacity-50"
                            style={{ backgroundColor: ZOBOX_BLUE }}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 shadow-lg p-4" style={{ borderTopColor: ZOBOX_BLUE }}>
            <div className="max-w-md mx-auto flex gap-3">
              <button
                onClick={() => setStep('review')}
                className="flex-1 px-4 py-3 text-gray-700 bg-gray-200 rounded-lg text-sm font-bold"
              >
                Back
              </button>
              <button
                onClick={() => setStep('media')}
                className="flex-1 px-4 py-3 text-white rounded-lg text-sm font-bold"
                style={{ backgroundColor: ZOBOX_BLUE }}
              >
                Add Media
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ReviewSummary = () => {
    const engineerAssignment = Object.values(responses).find(r => r.triggers_engineer)?.triggers_engineer;

    return (
      <div className="min-h-screen bg-gray-50 p-4 pb-24">
        <div className="max-w-md mx-auto">
          {isDeadPhone && (
            <div className="rounded-xl shadow-lg p-6 mb-6 text-center border-2 border-red-600 bg-red-50">
              <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-red-600" />
              <div className="text-red-900 text-sm mb-2 font-medium">DEAD PHONE DETECTED</div>
              <div className="text-red-800 text-lg font-bold mb-2">Requires L4 Diagnosis</div>
              <div className="text-red-700 text-xs">Device marked as Dead on Arrival</div>
            </div>
          )}

          {engineerAssignment && !isDeadPhone && (
            <div className="rounded-xl shadow-lg p-4 mb-6 bg-yellow-50 border-2 border-yellow-500">
              <div className="text-yellow-900 text-sm font-bold mb-1">Special Assignment Required</div>
              <div className="text-yellow-800 text-xs">This device will be assigned to {engineerAssignment} Profile Engineer</div>
            </div>
          )}

        <div className="bg-white rounded-xl shadow-lg p-5 mb-4">
          <h2 className="text-xl font-bold mb-2" style={{ color: ZOBOX_BLUE }}>Review Summary</h2>
          <p className="text-sm text-gray-600">Verify all responses</p>
        </div>

        {template.categories.map((category, idx) => {
          const categoryResponses = category.parameters
            .map(p => responses[p.parameter_id])
            .filter(Boolean);
          
          if (categoryResponses.length === 0) return null;

          return (
            <div key={idx} className="bg-white rounded-xl shadow-md p-4 mb-3 border-l-4" style={{ borderLeftColor: ZOBOX_BLUE }}>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                <span className="text-xl">{category.icon}</span>
                <div>
                  <div className="text-xs font-bold" style={{ color: ZOBOX_BLUE }}>Category {category.category_number}</div>
                  <h3 className="text-sm font-bold text-gray-900">{category.name}</h3>
                </div>
              </div>
              {categoryResponses.map((resp, i) => (
                <div key={i} className="text-xs py-2 border-b border-gray-100 last:border-0">
                  <div className="text-gray-500 font-medium">{resp.parameter_number}. {resp.parameter_name}</div>
                  <div className="text-gray-900 font-semibold mt-1">{resp.selected_value}</div>
                </div>
              ))}
            </div>
          );
        })}

          <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 shadow-lg p-4" style={{ borderTopColor: ZOBOX_BLUE }}>
            <div className="max-w-md mx-auto flex gap-3">
              <button
                onClick={() => {
                  setCurrentCategoryIndex(template.categories.length - 1);
                  setStep('iqc');
                }}
                className="flex-1 px-4 py-3 text-gray-700 bg-gray-200 rounded-lg text-sm font-bold"
              >
                Back
              </button>
              <button
                onClick={() => setStep('parts')}
                className="flex-1 px-4 py-3 text-white rounded-lg text-sm font-bold"
                style={{ backgroundColor: ZOBOX_BLUE }}
              >
                Part Request
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const MediaCapture = () => {
    const handleImageCapture = (e) => {
      const files = Array.from(e.target.files);
      setImages(prev => [...prev, ...files]);
    };
    
    const handleBack = () => {
      if (isDeadPhone) {
        setStep('iqc');
        setCurrentCategoryIndex(0);
      } else {
        setStep('review');
      }
    };

    const startVideoRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        videoRef.current.srcObject = stream;
        videoRef.current.play();

        const mediaRecorder = new MediaRecorder(stream);
        const chunks = [];

        mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/webm' });
          setVideoBlob(blob);
          stream.getTracks().forEach(track => track.stop());
        };

        mediaRecorderRef.current = mediaRecorder;
        mediaRecorder.start();
        setIsRecording(true);

        let time = 0;
        const interval = setInterval(() => {
          time++;
          setRecordingTime(time);
          if (time >= 30) {
            stopVideoRecording();
            clearInterval(interval);
          }
        }, 1000);
      } catch (error) {
        alert('Camera access denied');
      }
    };

    const stopVideoRecording = () => {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        setRecordingTime(0);
      }
    };

    return (
      <div className="min-h-screen bg-gray-50 p-4 pb-24">
        <div className="max-w-md mx-auto">
          {isDeadPhone && (
            <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 mb-4 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-sm font-bold text-red-900 mb-1">Dead Phone (DOA)</h3>
                <p className="text-xs text-red-700">Please capture images and video of the device before submitting. Device will be assigned to L4 Profile Engineer for diagnosis.</p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-lg p-5 mb-4 border-t-4" style={{ borderTopColor: ZOBOX_BLUE }}>
            <h2 className="text-xl font-bold mb-2" style={{ color: ZOBOX_BLUE }}>Capture Media</h2>
            <p className="text-sm text-gray-600">{isDeadPhone ? 'Required for Dead Phone documentation' : 'Add photos and video'}</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-900">Photos</h3>
              <label className="px-4 py-2 text-white rounded-lg text-sm font-bold cursor-pointer" style={{ backgroundColor: ZOBOX_BLUE }}>
                <Camera className="w-4 h-4 inline mr-1" />
                Take Photo
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={handleImageCapture}
                  className="hidden"
                />
              </label>
            </div>
            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {images.map((img, idx) => (
                  <div key={idx} className="relative aspect-square">
                    <img
                      src={URL.createObjectURL(img)}
                      alt=""
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <button
                      onClick={() => setImages(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 mb-4">
            <h3 className="text-base font-bold text-gray-900 mb-3">Video (30 sec max)</h3>
            {!videoBlob && !isRecording && (
              <button
                onClick={startVideoRecording}
                className="w-full py-3 bg-red-600 text-white rounded-lg flex items-center justify-center gap-2 text-sm font-bold"
              >
                <Video className="w-5 h-5" /> Start Recording
              </button>
            )}
            {isRecording && (
              <div>
                <video ref={videoRef} className="w-full rounded-lg mb-2" autoPlay muted />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-red-600 font-bold">{recordingTime}/30 sec</span>
                  <button
                    onClick={stopVideoRecording}
                    className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-bold"
                  >
                    Stop
                  </button>
                </div>
              </div>
            )}
            {videoBlob && !isRecording && (
              <div>
                <video src={URL.createObjectURL(videoBlob)} controls className="w-full rounded-lg" />
                <button
                  onClick={() => setVideoBlob(null)}
                  className="w-full mt-2 py-2 text-red-600 text-sm font-bold"
                >
                  Remove Video
                </button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 mb-4">
            <h3 className="text-base font-bold text-gray-900 mb-3">Notes</h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-blue-500"
              placeholder="Add observations..."
            />
          </div>

          <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 shadow-lg p-4" style={{ borderTopColor: ZOBOX_BLUE }}>
            <div className="max-w-md mx-auto flex gap-3">
              <button
                onClick={handleBack}
                className="flex-1 px-4 py-3 text-gray-700 bg-gray-200 rounded-lg text-sm font-bold"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || (isDeadPhone && images.length === 0 && !videoBlob)}
                className="flex-1 px-4 py-3 text-white rounded-lg text-sm font-bold disabled:opacity-50"
                style={{ backgroundColor: '#10b981' }}
              >
                {loading ? 'Submitting...' : 'Submit QC'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('user') || '{}');

      const startRes = await axios.post(
        `${API_URL}/api/iqc/start`,
        {
          device_imei: device.did,
          device_model: device.model,
          device_brand: device.brand,
          device_os: template.device_os,
          engineer_id: userData.id || userData.employee_id,
          engineer_name: userData.name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Get selected parts only (part-centric)
      const selectedParts = Object.entries(partsQuantities)
        .filter(([part, qty]) => qty > 0)
        .map(([part, qty]) => ({ part_name: part, quantity: qty }));

      await axios.post(
        `${API_URL}/api/iqc/submit`,
        {
          iqc_id: startRes.data.iqc_id,
          responses: Object.values(responses).map(r => ({
            parameter_id: r.parameter_id,
            parameter_name: r.parameter_name,
            selected_values: [r.selected_value],
            triggered_parts: r.triggered_parts || [],
            triggers_engineer: r.triggers_engineer || null,
            severity: 'Medium'
          })),
          general_notes: notes,
          selected_parts: selectedParts,
          is_dead_phone: isDeadPhone,
          auto_create_requisition: false // Will be created by approver after grade decision
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`QC Submitted!\n${isDeadPhone ? 'Status: Dead Phone - Assigned to L4' : 'Submitted for approval'}`);
      navigate('/trc');
    } catch (error) {
      console.error('Error:', error);
      alert('Submission failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 'scan': return <DeviceScan />;
      case 'iqc': return <IQCForm />;
      case 'review': return <ReviewSummary />;  // Review first
      case 'parts': return <PartsReview />;     // Then parts
      case 'media': return <MediaCapture />;
      default: return <DeviceScan />;
    }
  };

  if (loading && step === 'scan') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 mx-auto mb-4" style={{ borderColor: ZOBOX_BLUE }}></div>
          <div className="text-sm text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  return renderStep();
};

export default MobileIQC;
